import { Dialog } from '@angular/cdk/dialog';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  EMPTY,
  concatMap,
  distinctUntilChanged,
  exhaustMap,
  filter,
  of,
  pairwise,
  switchMap,
  take,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { CompanyType } from 'src/app/models/company.model';
import { InformationModalComponent } from '../../components/modals/information-modal/information-modal.component';
import { ApiFreezeService } from '../../services/api-freeze.service';
import { CompanyService } from '../../state-services/company.service';
import { UserService } from '../../state-services/user.service';
import * as CompanyActions from '../company/company.actions';
import * as ProfileActions from './profile.actions';
import { selectProfileData } from './profile.selector';

/**
 * How long to wait before asking again once the deadline has passed. Only reached when the
 * backend still honours the impersonation by then, i.e. when the browser clock runs ahead of the
 * server's, and it is what keeps that disagreement from turning the re-check into a loop.
 */
export const RECHECK_BACKOFF_MS = 5_000;

/**
 * When to ask the backend again about an impersonation that is running, or {@code null} when none
 * is: a user without one is never waited on, and never asks anything extra.
 */
export function impersonationRecheckDelayMs(
  expiresAt: string | undefined,
  now: number,
): number | null {
  if (!expiresAt) {
    return null;
  }
  const deadline = new Date(expiresAt).getTime();
  if (Number.isNaN(deadline)) {
    return null;
  }
  // Only a deadline that has passed waits for the backoff; one that is close is waited out.
  const remaining = deadline - now;
  return remaining > 0 ? remaining : RECHECK_BACKOFF_MS;
}

@Injectable()
export class ProfileEffects {
  /**
   * An impersonation stops being honoured at its deadline, and the pages of whoever was
   * impersonating would go on showing the other user's data. The deadline is only a hint to ask
   * again - the backend's answer decides - so a clock that disagrees cannot end one early.
   */
  impersonationDeadline$ = createEffect(() =>
    this.actions$.pipe(
      // A load that failed leaves the question open, so it arms the next attempt as well: the
      // deadline has passed by then, which is what the backoff is for.
      ofType(
        ProfileActions.loadingQuantumProfileSuccess,
        ProfileActions.loadingQuantumProfileError,
      ),
      withLatestFrom(this.store.select(selectProfileData)),
      switchMap(([, profile]) => {
        const delay = impersonationRecheckDelayMs(
          profile.impersonationExpiresAt,
          Date.now(),
        );
        return delay === null ? EMPTY : timer(delay);
      }),
      map(() => ProfileActions.loadingProfile()),
    ),
  );

  /** An impersonation that was running and is gone has ended, however it ended. */
  impersonationEnded$ = createEffect(
    () =>
      this.store.select(selectProfileData).pipe(
        map((profile) => !!profile.impersonatedBy),
        distinctUntilChanged(),
        pairwise(),
        filter(
          ([wasImpersonating, isImpersonating]) =>
            wasImpersonating && !isImpersonating,
        ),
        tap(() => this.announceImpersonationEnded()),
      ),
    { dispatch: false },
  );

  loadingProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.loadingProfile, ProfileActions.loginSuccess),
      concatMap(() => [
        ProfileActions.loadingQuantumProfile(),
        ProfileActions.loadingMsalActiveProfile(),
      ]),
    ),
  );
  loadQuantumProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.loadingQuantumProfile),
      exhaustMap(() =>
        this.userService.getProfile().pipe(
          concatMap((profile) => {
            const companyRole =
              typeof profile.companyRole === 'object'
                ? profile.companyRole?.role
                : profile.companyRole;
            const actions: Action[] = [
              ProfileActions.loadingQuantumProfileSuccess({
                quantumProfile: profile,
              }),
            ];
            const company = this.companyFromProfile(profile);

            if (company) {
              actions.push(
                CompanyActions.hydrateCompanyFromProfile({ company }),
              );
              actions.push(
                ProfileActions.loadingProfileRoleSuccess({
                  role: this.companyService.roleMapping(companyRole ?? 0),
                }),
              );
            }

            return actions;
          }),
          catchError((err) =>
            of(ProfileActions.loadingQuantumProfileError({ error: err })),
          ),
        ),
      ),
    ),
  );
  loadMSALProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.loadingMsalActiveProfile),
      exhaustMap(() => {
        const claims = this.authService.getIdClaims<any>();
        if (claims) {
          return of(
            ProfileActions.loadingMsalActiveProfileSuccess({
              msalProfile: {
                given_name: (claims['given_name'] as string) || '',
                family_name: (claims['family_name'] as string) || '',
                // B2C sometimes provides `emails: string[]` or a single `email`
                email:
                  (claims?.emails?.[0] as string) ||
                  (claims?.email as string) ||
                  '',
              },
            }),
          );
        }
        return of(
          ProfileActions.loadingMsalActiveProfileError({
            error: 'No active Account',
          }),
        );
      }),
    ),
  );
  editQuantumProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.editingQuantumProfile),
      withLatestFrom(this.store.select(selectProfileData)),
      exhaustMap(([action, profileData]) =>
        this.userService
          .editProfile({
            ...action.quantumProfile,
            firstName: action.quantumProfile.firstName || profileData.firstName,
            lastName: action.quantumProfile.lastName || profileData.lastName,
          })
          .pipe(
            map((profile) =>
              ProfileActions.editingQuantumProfileSuccess({
                updatedQuantumProfile: profile,
              }),
            ),
            catchError((error) =>
              of(ProfileActions.editingQuantumProfileError({ error: error })),
            ),
          ),
      ),
    ),
  );
  updateProfileRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updatingProfileRole),
      exhaustMap(({ companyId, memberId, role }) =>
        this.userService
          .changeProfileRole(memberId, companyId, {
            role: this.companyService.userRoleToCompanyRole(role),
          })
          .pipe(
            concatMap((role) => [
              ProfileActions.updatingProfileRoleSuccess({
                role: this.companyService.roleMapping(role.role),
              }),
              CompanyActions.loadingCompanyMembers({ companyId: companyId }),
            ]),
            catchError((error) =>
              of(ProfileActions.updatingProfileRoleError({ error: error })),
            ),
          ),
      ),
    ),
  );

  /**
   * Mirrors the flag to the interceptor, which cannot read it from the store itself without
   * closing a DI cycle on HTTP_INTERCEPTORS.
   */
  freezeApi$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProfileActions.freezingApiAfterImpersonation),
        tap(() => this.apiFreeze.freeze()),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private apiFreeze: ApiFreezeService,
    private userService: UserService,
    private companyService: CompanyService,
    private authService: AuthService,
    private store: Store,
    private dialog: Dialog,
    private translate: TranslateService,
  ) {}

  private announceImpersonationEnded() {
    this.dialog
      .open(InformationModalComponent, {
        // Either way out is a deliberate click: the page is showing two users at once.
        disableClose: true,
        data: {
          headerText: this.translate.instant('impersonation.ended.title'),
          bodyText: this.translate.instant('impersonation.ended.body'),
          cancelable: true,
          cancelButtonText: this.translate.instant('impersonation.ended.stay'),
          confirmButtonText: this.translate.instant(
            'impersonation.ended.confirm',
          ),
        },
      })
      .closed.pipe(take(1))
      .subscribe((result) => {
        if ((result as { cancel?: boolean } | undefined)?.cancel) {
          // Staying keeps what is on screen readable, e.g. to copy something out of a form. The
          // page asks for nothing more from then on, so it cannot mix the two users up.
          this.store.dispatch(ProfileActions.freezingApiAfterImpersonation());
          return;
        }
        this.reloadApplication();
      });
  }

  /** Everything on screen belonged to the other user, so the way back is a reload. */
  protected reloadApplication() {
    window.location.reload();
  }

  private companyFromProfile(
    profile: ProfileActions.QuantumProfile,
  ): CompanyType | null {
    const companyId = profile.companyId ?? profile.company?.id;

    if (!companyId) {
      return null;
    }

    return {
      id: companyId,
      name: profile.companyName ?? profile.company?.name ?? '',
      country: profile.country ?? profile.company?.country ?? '',
      stateOrProvince:
        profile.stateOrProvince ?? profile.company?.stateOrProvince ?? '',
      postalCode: profile.postalCode ?? profile.company?.postalCode ?? '',
      industry: profile.industry ?? profile.company?.industry ?? '',
      fieldsOfApplication:
        profile.fieldsOfApplication ??
        profile.company?.fieldsOfApplication ??
        (profile.fieldOfApplication ? [profile.fieldOfApplication] : []),
    };
  }
}
